/* eslint-disable react/no-did-mount-set-state */
import React from 'react';
import { graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';
import { toast } from 'react-toastify';
import _orderBy from 'lodash/orderBy';
import _uniqBy from 'lodash/uniqBy';
import { Button } from 'reactstrap';

import AppComponent from './core/AppComponent';
import Loading from './core/Loading';

import '../css/Clients.css';
import UserWithAlerts from './UserWithAlerts';
import FormModal from './core/FormModal';
import InviteUserForm from './InviteUserForm';
import FacilityInvites from './FacilityInvites';

class Users extends AppComponent {
  state = {
    openDropdown: -1,
    showModal: false,
    userToEdit: null,
  };

  toggleModal = () => this.setState({
    showModal: !this.state.showModal,
  });

  toggleAddModal = () => this.setState({
    showModal: !this.state.showModal,
    userToEdit: null,
  });

  toggleRow = index => () => {
    let newState = index;
    if (index === this.state.openDropdown) {
      newState = -1;
    }
    this.setState({
      openDropdown: newState,
    });
  };

  inviteUser = async ({ email, role }) => {
    await this.props.createInvite({
      variables: {
        email,
        role,
        facilityId: this.props.facilityId,
      },
    });
    this.toggleModal();
    this.props.facilityInvitesQuery.refetch();
    toast.success('Invited Staff');
  };

  editUser = async ({
    id, name, email, phone,
  }) => {
    await this.props.updateUser({
      variables: {
        id,
        name,
        email,
        phone,
      },
    });
    this.setState({
      userToEdit: null,
    });
    this.toggleModal();
    this.props.staffQuery.refetch();
    toast.success('Edited Staff');
  };

  deleteUser = async userId => {
    await this.props.deleteUser({
      variables: {
        userId,
      },
    });
    this.props.staffQuery.refetch();
    toast.success('Deleted Staff');
  };

  handleValidSubmit = async (e, values) => {
    if (this.state.userToEdit) {
      return this.editUser(values);
    }
    return this.inviteUser(values);
  };

  handleDeleteUser = userId => async () => this.deleteUser(userId);

  handleRevokeInvite = async inviteToken => {
    await this.props.revokeInvite({
      variables: { inviteToken },
    });
    this.props.facilityInvitesQuery.refetch();
    toast.success('Invite revoked');
  };

  handleEditUser = id => () => {
    this.setState({
      userToEdit: this.props.staffQuery.users.find(u => u.id === id),
    });
    this.toggleModal();
  };

  getUserAlerts = user => _uniqBy(user.claimedAlerts.concat(user.resolvedAlerts), a => a.id);

  getAlerts = (type, alerts) => {
    if (!alerts) {
      return [];
    }
    return alerts.filter(a => a.alertStatus === type);
  };

  render() {
    const { staffQuery, facilityInvitesQuery } = this.props;

    if (staffQuery.loading || facilityInvitesQuery.loading) {
      return (
        <Loading />
      );
    }

    const { facilityStaff = [] } = staffQuery;
    const { facilityInvites: invites } = facilityInvitesQuery;
    const { showModal, openDropdown, userToEdit } = this.state;
    const isEditing = !!userToEdit;
    const sortedStaff = _orderBy(facilityStaff, [s => s.name.toLowerCase()], ['asc']);

    return (
      <React.Fragment>

        <div className="text-right">
          <h2 className="nightingale-text float-left">Staff</h2>
          <Button color="info" onClick={this.toggleAddModal}><span className="cloudSense-Clients-title icon-user-plus" /></Button>
        </div>
        <hr />
        {
          !!invites.length &&
          <FacilityInvites invites={invites} onRevokeInvite={this.handleRevokeInvite} />
        }
        <div>
          {
            sortedStaff.length
              ? sortedStaff.map((user, index) =>
                (<UserWithAlerts
                  key={user.id}
                  user={user}
                  isStaff
                  role={user.role}
                  isShowing={openDropdown === index}
                  onDropdownClick={this.toggleRow(index)}
                  claimedAlerts={this.getAlerts('CLAIMED', this.getUserAlerts(user))}
                  confirmedAlerts={this.getAlerts('CONFIRMED', this.getUserAlerts(user))}
                  resolvedAlerts={this.getAlerts('RESOLVED', this.getUserAlerts(user))}
                  onDeleteClick={this.handleDeleteUser}
                  onEditClick={this.handleEditUser}
                />))
              : <span className="text-muted font-italic">No Staff. Add one!</span>
          }
        </div>

        <FormModal
          showModal={showModal}
          toggleModal={this.toggleModal}
          modalTitle={isEditing ? 'Edit Staff' : 'Invite Staff'}
          modalTitleIcon="icon-user-plus"
          affirmativeActionText="Send invite"
          onValidSubmit={this.handleValidSubmit}
        >
          <InviteUserForm user={isEditing ? userToEdit : null} />
        </FormModal>

      </React.Fragment>

    );
  }
}

const USERS_QUERY = gql`
  query getFacilityStaff($facilityId: String!){
    facilityStaff(facilityId: $facilityId) {
      id
      name
      role
      phone
      email
      clockedIn
      claimedAlerts {
        id
        alertStatus
        createdAt
        claimedAt
        resident {
          name
        }
      }
      resolvedAlerts {
        id
        alertStatus
        createdAt
        resolvedAt
        resident {
          name
        }
      }
    }
  }
`;

const INVITES_QUERY = gql`
  query getFacilityInvites($facilityId: String!){
    facilityInvites(facilityId: $facilityId) {
      id
      token
      email
      role
      createdAt
    }
  }
`;

const CREATE_INVITE = gql`
mutation CreateInvite($email: String!, $role: String!, $facilityId: String!) {
  createInvite(
    email: $email
    role: $role
    facilityId: $facilityId
  ) {
    id
  }
}
`;

const REVOKE_INVITE = gql`
mutation RevokeInvite($inviteToken: String!) {
  revokeInvite(
    inviteToken: $inviteToken
  ) {
    id
  }
}
`;

const UPDATE_USER = gql`
mutation UpdateUser($id: ID!, $email: String!, $name: String!, $phone: String!) {
  updateUser(where: {id: $id}, data: {
    email: $email
    name: $name
    phone: $phone
  }) {
    id
  }
}
`;

const DELETE_USER = gql`
mutation DeleteUser($userId: String!) {
  deleteUser(userId: $userId) {
    id
  }
}
`;


export default compose(
  graphql(USERS_QUERY, {
    name: 'staffQuery',
    options: props => ({
      variables: {
        facilityId: props.facilityId,
      },
      pollInterval: 5000,
    }),
  }),
  graphql(INVITES_QUERY, {
    name: 'facilityInvitesQuery',
    options: props => ({
      variables: {
        facilityId: props.facilityId,
      },
      pollInterval: 5000,
    }),
  }),
  graphql(CREATE_INVITE, {
    name: 'createInvite',
  }),
  graphql(REVOKE_INVITE, {
    name: 'revokeInvite',
  }),
  graphql(UPDATE_USER, {
    name: 'updateUser',
  }),
  graphql(DELETE_USER, {
    name: 'deleteUser',
  }),
)(Users);



// WEBPACK FOOTER //
// ./src/components/Users.js